import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useProject } from '../hooks/useProjects';
import { useActiveTabInView } from '../hooks/useActiveTabInView';
import { updateProjectCompany } from '../lib/projectsApi';
import { usePermissions } from '../hooks/usePermissions';
import { HealthBadge, StatusBadge } from '../components/projects/ProjectBadges';
import { CompanyBadge } from '../components/companies/CompanyBadge';
import { ProjectTasksTab } from '../components/projects/ProjectTasksTab';
import { ResourcesTab } from '../components/projects/ResourcesTab';
import { VaultTab } from '../components/projects/VaultTab';
import { ProjectOverviewTab } from '../components/projects/ProjectOverviewTab';
import { ProjectContextTab } from '../components/projects/ProjectContextTab';
import { ProjectTeamTab } from '../components/projects/ProjectTeamTab';
import { ProjectFinancialsTab } from '../components/projects/ProjectFinancialsTab';
import { EditProjectModal } from '../components/projects/EditProjectModal';
import { Skeleton, SkeletonTable, SkeletonText } from '../components/ui/Skeleton';
import { EditWithAIButton } from '../components/ui/EditWithAIButton';
import { MeetingNotesTab } from '../components/meetingNotes/MeetingNotesTab';
import { useI18n } from '../contexts/I18nContext';

// Tab order requested by the board (CLE-437): overview, then financials, then
// tasks, resources, team, and finally meeting notes. `notes` renders the
// "Notițe ședință" (meeting notes) tab, covering both the "notițe" and
// "ședință" items in the request. `vault` (credentials) sits next to resources
// since both are "things attached to the project" rather than work items.
// `context` (the project's story told to Firmio AI, injected into every AI
// interaction about the project) sits right after the overview: it is the
// first thing a new colleague should read.
const TABS = [
  'overview',
  'context',
  'financials',
  'tasks',
  'resources',
  'vault',
  'team',
  'notes',
] as const;
type Tab = (typeof TABS)[number];

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

export function ProjectDetailPage() {
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();
  const { data: project, isLoading, isError } = useProject(id);

  // Multi-company: the firm tag is DB-only metadata with its own PATCH (the KB
  // sync never touches it), editable straight from the header badge.
  const { can, isLoading: permsLoading } = usePermissions();
  const canEditProject = can('projects', 'canEdit');

  // Each project tab is gated on its own RBAC view flag so roles only ever see
  // the sections they are allowed to (CLE-431). A role with, say,
  // projects.canViewFinancials unchecked must not see the Financiar tab or its
  // content — the tab button and the panel are both dropped, not just disabled.
  const canViewTab: Record<Tab, boolean> = {
    overview: can('projects', 'canViewOverview'),
    // The context is the project's story — the same class of information as
    // the overview, so it shares that tab's view flag.
    context: can('projects', 'canViewOverview'),
    team: can('projects', 'canViewTeam'),
    financials: can('projects', 'canViewFinancials'),
    tasks: can('tasks', 'canView'),
    resources: can('projects', 'canViewResources'),
    vault: can('projects', 'canViewVault'),
    notes: can('projects', 'canViewMeetingNotes'),
  };
  const visibleTabs = TABS.filter((tabKey) => canViewTab[tabKey]);

  // The active tab lives in the URL (?tab=team, ?tab=financials, …) so it
  // survives reloads and can be shared/bookmarked. Deriving it from the URL on
  // every render (instead of freezing it in useState) also means the correct
  // tab shows up once RBAC finishes loading — an initial empty visibleTabs no
  // longer locks in the wrong tab. Falls back to the first visible tab when the
  // requested one is missing, invalid, or hidden by permissions.
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get('tab');
  const tab: Tab = isTab(urlTab) && visibleTabs.includes(urlTab) ? urlTab : visibleTabs[0];

  function selectTab(next: Tab) {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        params.set('tab', next);
        return params;
      },
      { replace: true },
    );
  }

  const [editing, setEditing] = useState(false);
  const qc = useQueryClient();

  const tabsRef = useActiveTabInView<HTMLElement>(tab, project);
  async function handleCompanyChange(companyId: string | null) {
    if (!id) return;
    await updateProjectCompany(id, companyId);
    await qc.invalidateQueries({ queryKey: ['project', id] });
    await qc.invalidateQueries({ queryKey: ['projects'] });
  }

  if (isLoading || permsLoading) {
    return (
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <Skeleton className="h-4" width={80} />
        <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
          <div className="space-y-3">
            <Skeleton className="h-8" width={280} />
            <div className="flex gap-2">
              <Skeleton className="h-5 rounded-full" width={72} />
              <Skeleton className="h-5 rounded-full" width={72} />
            </div>
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 rounded" width={88} />
            <Skeleton className="h-9 rounded" width={88} />
          </div>
        </div>
        {/* Tabs */}
        <div className="mt-6 flex gap-4 border-b border-border pb-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-4" width={72} />
          ))}
        </div>
        {/* Tab content: metadata + P&L table */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-border bg-card p-4">
            <SkeletonText lines={5} />
          </div>
          <div className="rounded-lg border border-border bg-card p-4">
            <SkeletonText lines={5} />
          </div>
        </div>
        <SkeletonTable className="mt-6" rows={5} columns={4} />
      </div>
    );
  }
  if (isError || !project) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <p className="text-red-600">{t('projects.notFound')}</p>
        <Link to="/projects" className="text-sm text-blue-600 underline">
          {t('projects.backToList')}
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <Link to="/projects" className="text-sm text-blue-600 underline">
        {t('projects.back')}
      </Link>

      <div className="mt-2 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{project.name}</h1>
          <div className="mt-2 flex items-center gap-2">
            <StatusBadge status={project.status} />
            <HealthBadge health={project.health} />
            <CompanyBadge
              company={project.company}
              onChange={canEditProject ? handleCompanyChange : undefined}
              title={t('projects.company.mainFirm')}
            />
          </div>
        </div>
        {/* Phones: two equal buttons side by side and the destructive one
            across the full width below, instead of a ragged wrap. */}
        <div className="grid w-full grid-cols-2 gap-2 sm:flex sm:w-auto sm:flex-wrap">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="inline-flex items-center justify-center gap-1.5 rounded-md bg-brand-600 px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700"
          >
            <Pencil size={15} />
            {t('projects.edit')}
          </button>
          <EditWithAIButton
            variant="secondary"
            label={t('projects.editWithAI')}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            prompt={`Vreau să editez proiectul "${project.name}". Te rog întreabă-mă ce să modific, apoi actualizează-l în Knowledge Base.`}
          />
          <EditWithAIButton
            variant="secondary"
            label={t('projects.deleteWithAI')}
            className="col-span-2 inline-flex items-center justify-center gap-1.5 rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 transition-colors hover:bg-red-50 sm:col-span-1"
            prompt={`Vreau să șterg proiectul "${project.name}". Te rog confirmă cu mine, apoi elimină-l din Knowledge Base.`}
          />
        </div>
      </div>

      <div className="mt-6 border-b border-gray-200">
        <nav ref={tabsRef} className="relative -mb-px flex gap-6 overflow-x-auto">
          {visibleTabs.map((tabKey) => (
            <button
              key={tabKey}
              type="button"
              onClick={() => selectTab(tabKey)}
              aria-current={tab === tabKey ? 'page' : undefined}
              className={`whitespace-nowrap border-b-2 px-1 py-2 text-sm font-medium ${
                tab === tabKey
                  ? 'border-brand-600 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t(`projects.tab.${tabKey}`)}
            </button>
          ))}
        </nav>
      </div>

      <div className="mt-6">
        {tab === 'overview' && canViewTab.overview && <ProjectOverviewTab project={project} />}
        {tab === 'context' && canViewTab.context && <ProjectContextTab project={project} />}
        {tab === 'team' && canViewTab.team && <ProjectTeamTab project={project} />}
        {tab === 'financials' && canViewTab.financials && (
          <ProjectFinancialsTab project={project} />
        )}
        {tab === 'tasks' && canViewTab.tasks && <ProjectTasksTab project={project} />}
        {tab === 'resources' && canViewTab.resources && <ResourcesTab project={project} />}
        {tab === 'vault' && canViewTab.vault && <VaultTab project={project} />}
        {tab === 'notes' && canViewTab.notes && (
          <MeetingNotesTab owner={{ projectId: project.id }} projectId={project.id} />
        )}
      </div>

      {editing && <EditProjectModal project={project} onClose={() => setEditing(false)} />}
    </div>
  );
}
